import { useState, useRef, useEffect } from "react";
import "../SchemeRegistration/SchemeRegistration.css";
import axios from "axios";
import io from "socket.io-client";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import { useSelector } from "react-redux";
import { selectMenuPermissions } from "../../../app/permissionsSlice";
import { selectActiveLocation } from "../../../app/locationSlice";
import { useDbContext } from "../../../context/DbContext";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import ActionToolbar from "../../NavBars/ActionToolbar";
import excl from "../../../assets/images/alerts/Exclamation.webp";
import SherComboBox from "../../ComboBoxes/SherComboBox";

function SchemeManagement({
  onClose,
  Open,
  zIndex,
  actionPermissions,
  schemeNameVal,
}) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const permissions = useSelector(selectMenuPermissions);
  const activeLocation = useSelector(selectActiveLocation);

  const [minimized, setMinimized] = useState(false);
  const [dataFound, setDataFound] = useState(false);
  const [editDisabled, setEditDisabled] = useState(true);
  const [deleteDisabled, setDeleteDisabled] = useState(true);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [schemesList, setSchemesList] = useState([]);
  const [currentSchemeId, setCurrentSchemeId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [nextSchemeId, setNextSchemeId] = useState("1");
  const [activeTab, setActiveTab] = useState("details");

  // Helper function to format date as YYYY-MM-DD
  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Get current date and date one year from now
  const getCurrentDate = () => formatDate(new Date());
  const getOneYearLater = () => {
    const today = new Date();
    const oneYearLater = new Date(
      today.getFullYear() + 1,
      today.getMonth(),
      today.getDate(),
    );
    return formatDate(oneYearLater);
  };

  // State object to store all form values
  const [formData, setFormData] = useState({
    schemeId: "",
    schemeName: schemeNameVal || "",
    validityType: "Fixed",
    fromDate: getCurrentDate(),
    toDate: getOneYearLater(),
    duration: "",
    dueDate: "",
    narration: "",
    Location: activeLocation ? activeLocation.id : "",
    schemeType: "Monthly",
    installmentAmount: "",
    maturityAmount: "",
    accountPrefix: "",
    accountSuffix: "",
    startingAccountNo: 0,
  });

  // Refs
  const schemeIdRef = useRef(null);
  const schemeNameRef = useRef(null);
  const schemeFromDateRef = useRef(null);
  const schemeToDateRef = useRef(null);
  const schemeDurationRef = useRef(null);
  const schemeDueDateRef = useRef(null);
  const installmentAmountRef = useRef(null);
  const maturityAmountRef = useRef(null);
  const schemeNarrationRef = useRef(null);
  const accountPrefixRef = useRef(null);
  const startingAccountNoRef = useRef(null);
  const accountSuffixRef = useRef(null);
  const saveButtonRef = useRef(null);
  const editButtonRef = useRef(null);

  // Configurable focus order
  const [focusOrder] = useState([
    "scheme-id",
    "scheme-name",
    "scheme-from-date",
    "scheme-to-date",
    "scheme-duration",
    "scheme-due-date",
    "installment-amount",
    "maturity-amount",
    "scheme-narration",
    "account-prefix",
    "starting-account-no",
    "account-suffix",
    "save-button",
    "edit-button",
  ]);

  // Map of element IDs to their refs
  const elementRefs = {
    "scheme-id": schemeIdRef,
    "scheme-name": schemeNameRef,
    "scheme-from-date": schemeFromDateRef,
    "scheme-to-date": schemeToDateRef,
    "scheme-duration": schemeDurationRef,
    "scheme-due-date": schemeDueDateRef,
    "installment-amount": installmentAmountRef,
    "maturity-amount": maturityAmountRef,
    "scheme-narration": schemeNarrationRef,
    "account-prefix": accountPrefixRef,
    "starting-account-no": startingAccountNoRef,
    "account-suffix": accountSuffixRef,
    "save-button": saveButtonRef,
    "edit-button": editButtonRef,
  };

  // Function to calculate months between two dates
  const calculateMonths = (startDate, endDate) => {
    if (!startDate || !endDate) return "0";

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end < start) return "0";

    let months = (end.getFullYear() - start.getFullYear()) * 12;
    months -= start.getMonth();
    months += end.getMonth();

    // Adjust for partial month
    if (end.getDate() < start.getDate()) {
      months--;
    }

    // If months is 0 but there's some duration, make it 1
    if (months === 0 && end.getTime() > start.getTime()) {
      months = 1;
    }

    return months > 0 ? months.toString() : "0";
  };

  // Function to calculate days between two dates
  const calculateDays = (startDate, endDate) => {
    if (!startDate || !endDate) return "0";

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end < start) return "0";

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays.toString();
  };

  // Fetch the last scheme ID to calculate next ID
  const fetchNextSchemeId = async () => {
    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/getLastSchemeId/${dbCode}`,
      );

      if (response.data.success) {
        const nextId = response.data.data?.lastSchemeId + 1 || 0;

        setNextSchemeId(nextId.toString());
        setFormData((prev) => ({
          ...prev,
          schemeId: nextId.toString(),
        }));
      }
    } catch (error) {
      console.error("Error fetching last scheme ID:", error);
      // Default to 1 on error
      const defaultId = "1";
      setNextSchemeId(defaultId);
      setFormData((prev) => ({
        ...prev,
        schemeId: defaultId,
      }));
    }
  };

  // Notify other components via WebSocket
  const notifySchemeUpdated = () => {
    try {
      const socket = io(apiBaseUrl);
      socket.emit("SchemeUpdated", { success: true });
      setTimeout(() => socket.disconnect(), 1000);
    } catch (err) {
      console.error("Socket emit error:", err);
    }
  };

  // Fetch all schemes for ComboBox
  const fetchSchemes = async () => {
    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/getSchemes/${dbCode}`,
      );

      if (response.data.success) {
        // Format for SherComboBox: { value, label } objects
        const formattedSchemes = response.data.data.map((item) => ({
          value: item.SchemeId,
          label: item.SchemeName,
        }));

        setSchemesList(formattedSchemes);
      }
    } catch (error) {
      console.error("Error fetching schemes:", error);
    }
  };

  // Fetch scheme by ID
  const fetchSchemeById = async (schemeId) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${apiBaseUrl}/main/findSchemeById/${dbCode}/${schemeId}`,
      );

      if (response.data.success && response.data.data) {
        const scheme = response.data.data;
        setFormData({
          schemeId: scheme.SchemeId?.toString() || "",
          schemeName: [scheme.SchemeId, scheme.SchemeName],
          validityType: scheme.ValidityType || "Fixed",
          fromDate: formatDate(scheme.FromDate) || getCurrentDate(),
          toDate: scheme.ToDate ? formatDate(scheme.ToDate) : "",
          duration: scheme.Duration?.toString() || "",
          dueDate: scheme.DueDay?.toString() || "",
          narration: scheme.Narration || "",
          Location: activeLocation ? activeLocation.id : "",
          schemeType: scheme.SchemeType || "Monthly",
          installmentAmount: scheme.InstallmentAmount?.toString() || "",
          maturityAmount: scheme.MaturityAmount?.toString() || "",
          accountPrefix: scheme.AccountPrefix || "",
          accountSuffix: scheme.AccountSuffix || "",
          startingAccountNo: scheme.StartingAccountNo ?? 0,
        });
        setCurrentSchemeId(scheme.SchemeId);
        setDataFound(true);
        setEditDisabled(false);
        setDeleteDisabled(false);
      }
    } catch (error) {
      console.error("Error fetching scheme:", error);
      alert("Failed to load scheme data.");
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize component
  useEffect(() => {
    // Fetch last scheme ID first
    fetchNextSchemeId();

    // Focus on the first element
    if (focusOrder.length > 0) {
      const firstElementId = focusOrder[0];
      const firstElementRef = elementRefs[firstElementId];

      if (firstElementRef && firstElementRef.current) {
        setTimeout(() => {
          firstElementRef.current.focus();
        }, 100);
      }
    }

    // Fetch schemes list
    fetchSchemes();
  }, []);

  // WebSocket listener for real-time scheme updates
  useEffect(() => {
    const socket = io(apiBaseUrl);
    socket.on("SchemeUpdated", () => {
      fetchSchemes();
      fetchNextSchemeId();
    });
    return () => {
      socket.disconnect();
    };
  }, [apiBaseUrl, dbCode]);

  // Update duration and maturity amount when dates, type, or installment amount changes
  useEffect(() => {
    let durationVal = formData.duration || "0";
    if (formData.validityType === "Fixed") {
      if (formData.schemeType === "Daily") {
        durationVal = calculateDays(formData.fromDate, formData.toDate);
      } else {
        durationVal = calculateMonths(formData.fromDate, formData.toDate);
      }
    }

    const durationNum = parseInt(durationVal) || 0;
    const installmentNum = parseFloat(formData.installmentAmount) || 0;
    const calculatedMaturity = durationNum * installmentNum;

    setFormData((prev) => ({
      ...prev,
      duration: prev.validityType === "Fixed" ? durationVal : prev.duration,
      maturityAmount: calculatedMaturity > 0 ? calculatedMaturity.toFixed(2) : prev.maturityAmount,
    }));
  }, [formData.fromDate, formData.toDate, formData.schemeType, formData.installmentAmount, formData.validityType]);

  // Handle input change
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

    // Handle Enter key press to move focus across tabs and fields
  const handleKeyDown = (e, currentElementId) => {
    if (e.key === "Enter") {
      e.preventDefault();

      const currentIndex = focusOrder.indexOf(currentElementId);

      if (currentIndex !== -1) {
        let nextIndex = currentIndex + 1;
        while (nextIndex < focusOrder.length) {
          const nextElementId = focusOrder[nextIndex];

          // Skip due-date if not Monthly scheme
          if (nextElementId === "scheme-due-date" && formData.schemeType !== "Monthly") {
            nextIndex++;
            continue;
          }

          // Transitioning to series tab fields: automatically activate the series tab
          if (["account-prefix", "starting-account-no", "account-suffix"].includes(nextElementId)) {
            setActiveTab("series");
            setTimeout(() => {
              const targetRef = elementRefs[nextElementId];
              if (targetRef && targetRef.current) {
                targetRef.current.focus();
              }
            }, 60);
            return;
          }

          // If moving to save-button or edit-button
          if (nextElementId === "save-button" || nextElementId === "edit-button") {
            if (nextElementId === "save-button" && (saveDisabled || dataFound)) {
              if (focusOrder.includes("edit-button")) {
                const editIndex = focusOrder.indexOf("edit-button");
                if (editIndex > nextIndex) {
                  nextIndex = editIndex;
                  const editBtn = elementRefs["edit-button"];
                  if (editBtn && editBtn.current) {
                    editBtn.current.focus();
                    return;
                  }
                }
              }
            }
            const btnRef = elementRefs[nextElementId];
            if (btnRef && btnRef.current) {
              btnRef.current.focus();
              return;
            }
          }

          // Transitioning to details tab fields (if user is on series and loops back)
          if ([
            "scheme-id", "scheme-name", "scheme-from-date", "scheme-to-date",
            "scheme-duration", "scheme-due-date", "installment-amount",
            "maturity-amount", "scheme-narration"
          ].includes(nextElementId)) {
            if (activeTab !== "details") {
              setActiveTab("details");
            }
          }

          const nextElementRef = elementRefs[nextElementId];

          if (nextElementRef && nextElementRef.current) {
            nextElementRef.current.focus();

            if (
              nextElementId === "scheme-name" &&
              schemeNameRef.current?.inputRef?.current
            ) {
              schemeNameRef.current.inputRef.current.focus();
            }

            break;
          }

          nextIndex++;
        }

        if (nextIndex >= focusOrder.length) {
          const firstElementId = focusOrder[0];
          const firstElementRef = elementRefs[firstElementId];
          setActiveTab("details");
          setTimeout(() => {
            if (firstElementRef && firstElementRef.current) {
              firstElementRef.current.focus();
            }
          }, 60);
        }
      }
    }
  };

  // Handle due date input - validate it's a day number (1-31)
  const handleDueDateChange = (value) => {
    const numericValue = value.replace(/[^0-9]/g, "");

    let dayNumber = parseInt(numericValue, 10);

    if (isNaN(dayNumber)) {
      handleInputChange("dueDate", "");
    } else if (dayNumber < 1) {
      handleInputChange("dueDate", "1");
    } else if (dayNumber > 31) {
      handleInputChange("dueDate", "31");
    } else {
      handleInputChange("dueDate", numericValue);
    }
  };

  // Helper function to extract scheme name from array, object, or string
  const extractSchemeName = (schemeName) => {
    if (Array.isArray(schemeName)) {
      return schemeName[1] || schemeName[0] || "";
    }
    if (schemeName && typeof schemeName === "object") {
      return schemeName.label || schemeName.name || "";
    }
    return schemeName || "";
  };

  // Handle save/insert
  const handleSave = async () => {
    //console.log("Save clicked with data:", formData);

    setSaveDisabled(true);

    try {
      // Extract scheme name
      const schemeNameValue = extractSchemeName(formData.schemeName);

      // Validate form data
      if (!schemeNameValue || schemeNameValue.trim() === "") {
        alert("Please enter scheme name.");
        setSaveDisabled(false);
        return;
      }

      if (!formData.fromDate) {
        alert("Please select start date.");
        setSaveDisabled(false);
        return;
      }

      if (formData.validityType === "Fixed" && !formData.toDate) {
        alert("Please select end date.");
        setSaveDisabled(false);
        return;
      }
      if (formData.schemeType === "Monthly" && !formData.dueDate) {
        alert("Due day is Required for Monthly schemes");
        setSaveDisabled(false);
        return;
      }

      if (!formData.installmentAmount || parseFloat(formData.installmentAmount) <= 0) {
        alert("Installment amount is required and must be greater than 0");
        setSaveDisabled(false);
        return;
      }

      // Validate date range if toDate is provided
      if (formData.toDate && formData.fromDate) {
        const fromDate = new Date(formData.fromDate);
        const toDate = new Date(formData.toDate);

        if (toDate < fromDate) {
          alert("End date must be after start date.");
          setSaveDisabled(false);
          return;
        }
      }

      // Validate due date
      if (formData.schemeType === "Monthly" && formData.dueDate) {
        const dueDay = parseInt(formData.dueDate);
        if (isNaN(dueDay) || dueDay < 1 || dueDay > 31) {
          alert("Due date must be a number between 1 and 31.");
          setSaveDisabled(false);
          return;
        }
      }

      // Validate duration
      if (!formData.duration || parseInt(formData.duration) <= 0) {
        alert(`Duration must be greater than 0 ${formData.schemeType === "Daily" ? "days" : "months"}.`);
        setSaveDisabled(false);
        return;
      }

      // Prepare data for API
      const schemeData = {
        schemeName: schemeNameValue,
        validityType: formData.validityType || "Fixed",
        fromDate: formData.fromDate,
        toDate: formData.toDate || null,
        duration: formData.duration,
        dueDate: formData.schemeType === "Monthly" ? (formData.dueDate || null) : null,
        narration: formData.narration || "",
        Location: formData.Location || null,
        schemeType: formData.schemeType || "Monthly",
        installmentAmount: formData.installmentAmount || 0,
        maturityAmount: formData.maturityAmount || 0,
        accountPrefix: formData.accountPrefix || "",
        accountSuffix: formData.accountSuffix || "",
        startingAccountNo: parseInt(formData.startingAccountNo, 10) || 0,
      };

      //console.log("Submitting to API:", schemeData);

      // Make POST request
      const response = await axios.post(
        `${apiBaseUrl}/main/insertSchemeManagement/${dbCode}`,
        schemeData,
      );

      // Handle response
      if (response.data.success) {
        alert("Scheme created successfully!");

        if (schemeNameVal) {
          onClose();
        }

        // Refresh the next scheme ID
        await fetchNextSchemeId();
        handleClear();

        // Refresh schemes list
        await fetchSchemes();
        notifySchemeUpdated();
      } else {
        if (response.data.message === "Duplicate scheme name") {
          alert(
            "This scheme name already exists. Please use a different name.",
          );
        } else {
          alert("Error creating scheme. Please try again.");
        }
        setSaveDisabled(false);
      }
    } catch (error) {
      setSaveDisabled(false);

      if (error.response) {
        if (error.response.data.message === "Duplicate scheme name") {
          alert(
            "This scheme name already exists. Please use a different name.",
          );
        } else {
          console.error("Server error:", error.response.data);
          alert(
            `Error: ${error.response.data.message || "Failed to create scheme"}`,
          );
        }
      } else if (error.request) {
        console.error("No response:", error.request);
        alert("No response from server. Please check your connection.");
      } else {
        console.error("Error:", error.message);
        alert("An error occurred. Please try again.");
      }
    }
  };

  // Handle clear form
  const handleClear = () => {
    //console.log("Clear clicked");
    const currentDate = getCurrentDate();
    const oneYearLater = getOneYearLater();

    // Reset form with next scheme ID
    setFormData({
      schemeId: nextSchemeId,
      schemeName: "",
      validityType: "Fixed",
      fromDate: currentDate,
      toDate: oneYearLater,
      duration: calculateMonths(currentDate, oneYearLater),
      dueDate: "",
      narration: "",
      Location: activeLocation ? activeLocation.id : "",
      schemeType: "Monthly",
      installmentAmount: "",
      maturityAmount: "",
      accountPrefix: "",
      accountSuffix: "",
      startingAccountNo: 0,
    });

    // Clear ref values
    if (schemeIdRef.current) schemeIdRef.current.value = nextSchemeId;
    if (schemeNameRef.current && schemeNameRef.current.clearValue) {
      schemeNameRef.current.clearValue();
    }
    if (schemeFromDateRef.current)
      schemeFromDateRef.current.value = currentDate;
    if (schemeToDateRef.current) schemeToDateRef.current.value = oneYearLater;
    if (schemeDurationRef.current)
      schemeDurationRef.current.value = calculateMonths(
        currentDate,
        oneYearLater,
      );
    if (schemeDueDateRef.current) schemeDueDateRef.current.value = "";
    if (installmentAmountRef.current) installmentAmountRef.current.value = "";
    if (maturityAmountRef.current) maturityAmountRef.current.value = "";
    if (schemeNarrationRef.current) schemeNarrationRef.current.value = "";

    // Reset states
    setSaveDisabled(false);
    setEditDisabled(true);
    setDeleteDisabled(true);
    setDataFound(false);
    setCurrentSchemeId(null);
    setActiveTab("details");

    // Focus back to first element
    setTimeout(() => {
      if (schemeIdRef.current) {
        schemeIdRef.current.focus();
      }
    }, 100);
    fetchNextSchemeId();
  };

  // Handle find scheme
  const handleFind = async () => {
    // Extract scheme name from current form data
    const schemeNameValue = extractSchemeName(formData.schemeName);

    if (!schemeNameValue || schemeNameValue.trim() === "") {
      alert("Select or enter Scheme Name");
      return;
    }

    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/findSchemeByName/${dbCode}/${encodeURIComponent(schemeNameValue)}`,
      );

      if (response.data.success && response.data.data) {
        const scheme = response.data.data;
        setFormData({
          schemeId: scheme.SchemeId?.toString() || "",
          schemeName: [scheme.SchemeId, scheme.SchemeName],
          validityType: scheme.ValidityType || "Fixed",
          fromDate: formatDate(scheme.FromDate) || getCurrentDate(),
          toDate: scheme.ToDate ? formatDate(scheme.ToDate) : "",
          duration: scheme.Duration?.toString() || "",
          dueDate: scheme.DueDay?.toString() || "",
          narration: scheme.Narration || "",
          Location: activeLocation ? activeLocation.id : "",
          schemeType: scheme.SchemeType || "Monthly",
          installmentAmount: scheme.InstallmentAmount?.toString() || "",
          maturityAmount: scheme.MaturityAmount?.toString() || "",
          accountPrefix: scheme.AccountPrefix || "",
          accountSuffix: scheme.AccountSuffix || "",
          startingAccountNo: scheme.StartingAccountNo ?? 0,
        });
        setCurrentSchemeId(scheme.SchemeId);
        setDataFound(true);
        setEditDisabled(false);
        setDeleteDisabled(false);
      } else {
        alert("Scheme not found.");
      }
    } catch (error) {
      console.error("Error finding scheme:", error);
      alert("Failed to find scheme.");
    }
  };

  // Handle edit scheme
  const handleEdit = async () => {
    //console.log("Edit clicked with formData:", formData);
    //console.log("Current scheme ID:", currentSchemeId);

    if (!formData.schemeId || formData.schemeId.toString().trim() === "") {
      alert("No scheme selected for editing.");
      return;
    }

    // Validate form data
    const schemeNameValue = extractSchemeName(formData.schemeName);

    if (!schemeNameValue || schemeNameValue.trim() === "") {
      alert("Please enter scheme name.");
      return;
    }

    if (!formData.fromDate) {
      alert("Please select start date.");
      return;
    }

    if (formData.validityType === "Fixed" && !formData.toDate) {
      alert("Please select end date.");
      return;
    }

    if (formData.schemeType === "Monthly" && !formData.dueDate) {
      alert("Due day is Required for Monthly schemes");
      return;
    }

    if (!formData.installmentAmount || parseFloat(formData.installmentAmount) <= 0) {
      alert("Installment amount is required and must be greater than 0");
      return;
    }

    // Validate date range if toDate is provided
    if (formData.toDate && formData.fromDate) {
      const fromDate = new Date(formData.fromDate);
      const toDate = new Date(formData.toDate);
      if (toDate < fromDate) {
        alert("End date must be after start date.");
        return;
      }
    }

    // Validate due date
    if (formData.schemeType === "Monthly" && formData.dueDate) {
      const dueDay = parseInt(formData.dueDate);
      if (isNaN(dueDay) || dueDay < 1 || dueDay > 31) {
        alert("Due date must be a number between 1 and 31.");
        return;
      }
    }

    // Validate duration
    if (!formData.duration || parseInt(formData.duration) <= 0) {
      alert(`Duration must be greater than 0 ${formData.schemeType === "Daily" ? "days" : "months"}.`);
      return;
    }

    try {
      setEditDisabled(true);

      // Prepare update data
      const updateData = {
        schemeId: parseInt(formData.schemeId),
        schemeName: schemeNameValue,
        validityType: formData.validityType || "Fixed",
        fromDate: formData.fromDate,
        toDate: formData.toDate || null,
        duration: parseInt(formData.duration) || 0,
        dueDate: formData.schemeType === "Monthly" ? (parseInt(formData.dueDate) || null) : null,
        narration: formData.narration || "",
        Location: formData.Location || null,
        schemeType: formData.schemeType || "Monthly",
        installmentAmount: parseFloat(formData.installmentAmount) || 0,
        maturityAmount: parseFloat(formData.maturityAmount) || 0,
        accountPrefix: formData.accountPrefix || "",
        accountSuffix: formData.accountSuffix || "",
        startingAccountNo: parseInt(formData.startingAccountNo, 10) || 0,
      };

      //console.log("Update data being sent:", updateData);

      // Make update request
      const response = await axios.put(
        `${apiBaseUrl}/main/updateSchemeMngt/${dbCode}`,
        updateData,
      );

      if (response.data.success) {
        alert("Scheme updated successfully!");
        await fetchSchemes();
        notifySchemeUpdated();
        setEditDisabled(false);
      } else {
        // Handle duplicate name error
        if (response.data.message === "Duplicate scheme name") {
          alert(
            "This scheme name already exists. Please use a different name.",
          );
        } else {
          alert(
            "Failed to update scheme: " +
              (response.data.message || "Unknown error"),
          );
        }
        setEditDisabled(false);
      }
    } catch (error) {
      console.error("Error updating scheme:", error);

      if (error.response) {
        if (error.response.data.message === "Duplicate scheme name") {
          alert(
            "This scheme name already exists. Please use a different name.",
          );
        } else {
          alert(
            "Failed to update scheme: " +
              (error.response.data.message || "Unknown error"),
          );
        }
      } else {
        alert("Failed to update scheme: " + (error.message || "Unknown error"));
      }
      setEditDisabled(false);
    }
  };

  // Handle delete scheme
  const handleDelete = async () => {
    //console.log("Delete clicked");

    if (!formData.schemeId || formData.schemeId.toString().trim() === "") {
      alert("No scheme selected for deletion.");
      return;
    }

    try {
      // Check if any accounts exist in scheme_registration for this schemeId
      const checkResponse = await axios.get(
        `${apiBaseUrl}/main/checkSchemeInUse/${dbCode}/${formData.schemeId}`,
      );

      if (checkResponse.data?.inUse) {
        alert("Cannot delete! Customers are already registered under this scheme.");
        return;
      }
    } catch (error) {
      console.error("Error checking scheme usage:", error);
    }

    if (window.confirm("Are you sure you want to delete this scheme?")) {
      try {
        setDeleteDisabled(true);

        const response = await axios.delete(
          `${apiBaseUrl}/main/deleteSchemeMngnt/${dbCode}/${formData.schemeId}?locationId=${activeLocation ? activeLocation.id : ''}`,
        );

        if (response.data.success) {
          alert("Scheme deleted successfully!");

          // Refresh the next scheme ID
          await fetchNextSchemeId();
          handleClear();
          await fetchSchemes();
          notifySchemeUpdated();
        } else {
          alert(response.data.message || "Failed to delete scheme.");
          setDeleteDisabled(false);
        }
      } catch (error) {
        console.error("Error deleting scheme:", error);
        alert(
          error.response?.data?.message ||
            error.message ||
            "Failed to delete scheme.",
        );
        setDeleteDisabled(false);
      }
    }
  };

  const handleFirst = async () => {
    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/getFirstScheme/${dbCode}`,
      );

      if (response.data.success && response.data.data) {
        loadSchemeData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching first scheme:", error);
    }
  };
  const handlePrevious = async () => {
    //console.log("prev clicked")

    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/getPreviousScheme/${dbCode}/${formData.schemeId}`,
      );

      if (response.data.success && response.data.data) {
        loadSchemeData(response.data.data);
      } else {
        // Optionally show a message or just stay on current record
        //console.log("No previous scheme found");
      }
    } catch (error) {
      console.error("Error fetching previous scheme:", error);
    }
  };

  const handleNext = async () => {
    //console.log("next clicked")
    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/getNextScheme/${dbCode}/${formData.schemeId}`,
      );

      if (response.data.success && response.data.data) {
        loadSchemeData(response.data.data);
      } else {
        // Optionally show a message or just stay on current record
        //console.log("No next scheme found");
      }
    } catch (error) {
      console.error("Error fetching next scheme:", error);
    }
  };

  const handleLast = async () => {
    try {
      const response = await axios.get(
        `${apiBaseUrl}/main/getLastScheme/${dbCode}`,
      );

      if (response.data.success && response.data.data) {
        loadSchemeData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching last scheme:", error);
    }
  };
  // Helper function to load scheme data
  const loadSchemeData = (scheme) => {
    setFormData({
      schemeId: scheme.SchemeId?.toString() || "",
      schemeName: [scheme.SchemeId, scheme.SchemeName],
      validityType: scheme.ValidityType || "Fixed",
      fromDate: formatDate(scheme.FromDate) || getCurrentDate(),
      toDate: scheme.ToDate ? formatDate(scheme.ToDate) : "",
      duration: scheme.Duration?.toString() || "",
      dueDate: scheme.DueDay?.toString() || "",
      narration: scheme.Narration || "",
      Location: activeLocation ? activeLocation.id : "",
      schemeType: scheme.SchemeType || "Monthly",
      installmentAmount: scheme.InstallmentAmount?.toString() || "",
      maturityAmount: scheme.MaturityAmount?.toString() || "",
      accountPrefix: scheme.AccountPrefix || "",
      accountSuffix: scheme.AccountSuffix || "",
      startingAccountNo: scheme.StartingAccountNo ?? 0,
    });
    setCurrentSchemeId(scheme.SchemeId);
    setDataFound(true);
    setEditDisabled(false);
    setDeleteDisabled(false);
  };

  // Function to update SecondaryNav button refs
  const updateNavRefs = (refs) => {
    if (refs.saveButtonRef) {
      saveButtonRef.current = refs.saveButtonRef.current;
    }
    if (refs.editButtonRef) {
      editButtonRef.current = refs.editButtonRef.current;
    }
  };

  // Function to handle free-text SherComboBox input
  const handleComboBoxChange = (text) => {
    handleInputChange("schemeName", text);
  };

  // Helper to format display date like "24/Jul/2026"
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d)) return "—";
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${String(d.getDate()).padStart(2, "0")}/${months[d.getMonth()]}/${d.getFullYear()}`;
  };

  // Handle print
  const handlePrint = () => {
    //console.log("Print clicked");
    window.print();
  };

  return (
    <>
      {/* ── Outer window ──────────────────────────────────────────────────── */}
      <div
        className={`bg-white rounded-[10px] shadow-2xl flex flex-col absolute transition-all duration-300 overflow-hidden border border-gray-200 ${minimized ? "minimized" : ""}`}
        style={{ zIndex, width: 840, height: 590 }}
      >
        {/* Window header */}
        <WindowHeader
          title="Scheme Management"
          logo={headeLogo}
          onClose={onClose}
          className="bg-[#1e40af] text-white h-8 shrink-0"
          titleClassName="text-[13px] font-semibold font-Inter"
          closeBtnClassName="h-5 w-5 my-auto mr-1 bg-red-600 hover:bg-red-700 rounded-sm"
        />

        {/* Action toolbar */}
        <ActionToolbar
          width="100%"
          height="auto"
          size="small"
          iconSize={14}
          bgColor="#f0f4ff"
          navBgColor="#1a3abf"
          navIconColor="#ffffff"
          buttonGap="5px"
          arrowGap="2%"
          showSave={true}
          showPrint={true}
          showClear={true}
          showFind={true}
          showEdit={true}
          showDelete={true}
          showExit={true}
          showNavigation={true}
          saveDisabled={saveDisabled || isLoading}
          editDisabled={editDisabled || isLoading}
          deleteDisabled={deleteDisabled || isLoading}
          dataFound={dataFound}
          permissions={permissions}
          permissionKey="SchemeManagement"
          onSave={handleSave}
          onPrint={handlePrint}
          onClear={handleClear}
          onFind={handleFind}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onExit={onClose}
          onFirst={handleFirst}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onLast={handleLast}
          onRefsUpdate={updateNavRefs}
        />

        {/* ── Body Area with Cards ────────────────────────────────────────── */}
        <div className="flex flex-1 gap-3 p-3 bg-white overflow-hidden box-border" style={{ height: 515 }}>
          {/* ── LEFT CARD: SCHEME DETAILS ─────────────────────────────────── */}
          <div className="flex-1 border border-gray-200 rounded-lg p-2.5 flex flex-col gap-1.5 bg-white overflow-y-auto box-border h-full">
            {/* Tab Header Bar */}
            <div className="flex items-center justify-between pb-1 border-b border-slate-200 mb-1">
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setActiveTab("details")}
                  className={`px-2.5 py-1 text-[11px] font-bold font-Inter flex items-center gap-1.5 rounded transition-all duration-150 cursor-pointer ${
                    activeTab === "details"
                      ? "bg-[#1a3abf] text-white shadow-3xs"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200/80"
                  }`}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  Scheme Details
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab("series")}
                  className={`px-2.5 py-1 text-[11px] font-bold font-Inter flex items-center gap-1.5 rounded transition-all duration-150 cursor-pointer ${
                    activeTab === "series"
                      ? "bg-[#1a3abf] text-white shadow-3xs"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200/80"
                  }`}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="4" y1="9" x2="20" y2="9" />
                    <line x1="4" y1="15" x2="20" y2="15" />
                    <line x1="10" y1="3" x2="8" y2="21" />
                    <line x1="16" y1="3" x2="14" y2="21" />
                  </svg>
                  Account Series
                  {(formData.accountPrefix || formData.accountSuffix || formData.startingAccountNo > 0) && (
                    <span className={`text-[9.5px] px-1.5 py-0.2 rounded font-mono font-bold ${
                      activeTab === "series" ? "bg-white/25 text-white" : "bg-blue-100 text-blue-700 border border-blue-200"
                    }`}>
                      {formData.accountPrefix || ""}{formData.startingAccountNo > 0 ? formData.startingAccountNo : 1}{formData.accountSuffix || ""}
                    </span>
                  )}
                </button>
              </div>

              <span className="text-[10px] font-semibold text-slate-400 font-Inter">
                {activeTab === "details" ? "Master Form" : "Series Settings"}
              </span>
            </div>

            {/* TAB 1: SCHEME DETAILS */}
            {activeTab === "details" && (
              <>
                <div className="grid grid-cols-2 gap-x-3.5 gap-y-2 mt-1">
              
              {/* Scheme ID */}
              <div className="flex flex-col gap-1">
                <label
                  className="text-[11.5px] font-semibold text-slate-600 font-Inter"
                  htmlFor="scheme-id"
                >
                  Scheme ID
                </label>
                <input
                  id="scheme-id"
                  ref={schemeIdRef}
                  type="text"
                  className="w-full border border-slate-300 rounded-[6px] outline-none px-2.5 text-[12.5px] font-Inter box-border bg-[#fef9c3] font-bold text-[#854d0e] border-[#fef08a] focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12 read-only:bg-slate-50 read-only:text-slate-500 disabled:bg-slate-50 disabled:text-slate-500"
                  style={{ height: 27 }}
                  value={formData.schemeId}
                  onChange={(e) => handleInputChange("schemeId", e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, "scheme-id")}
                  readOnly={dataFound}
                />
              </div>

              {/* Scheme Type Selector */}
              <div className="flex flex-col gap-1">
                <label
                  className="text-[11.5px] font-semibold text-slate-600 font-Inter"
                  htmlFor="scheme-type"
                >
                  Scheme Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="scheme-type"
                  className="w-full border border-slate-300 rounded-[6px] outline-none px-2.5 text-[12px] font-Inter box-border bg-white text-slate-800 transition-all duration-200 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12"
                  style={{ height: 27 }}
                  value={formData.schemeType}
                  onChange={(e) => handleInputChange("schemeType", e.target.value)}
                  disabled={isLoading}
                >
                  <option value="Monthly">Monthly Installment</option>
                  <option value="Daily">Daily Installment</option>
                </select>
              </div>

              {/* Validity Model Selector */}
              <div className="col-span-2 flex flex-col gap-1">
                <label
                  className="text-[11.5px] font-semibold text-slate-600 font-Inter"
                  htmlFor="validity-type"
                >
                  Scheme Validity Model <span className="text-red-500">*</span>
                </label>
                <select
                  id="validity-type"
                  className="w-full border border-slate-300 rounded-[6px] outline-none px-2.5 text-[12px] font-Inter box-border bg-white text-slate-800 transition-all duration-200 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12"
                  style={{ height: 27 }}
                  value={formData.validityType}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      validityType: val,
                      duration: val === "Tenure" ? (prev.duration || "12") : prev.duration
                    }));
                  }}
                  disabled={isLoading}
                >
                  <option value="Fixed">Fixed Period (Fixed calendar dates for all customers)</option>
                  <option value="Tenure">Rolling Tenure (Valid for specified duration from customer enrollment date)</option>
                </select>
              </div>

              {/* Scheme Name */}
              <div className="col-span-2 flex flex-col gap-1">
                <label
                  className="text-[11.5px] font-semibold text-slate-600 font-Inter"
                  htmlFor="scheme-name"
                >
                  Scheme Name <span className="text-red-500">*</span>
                </label>
                <div className="w-full">
                  <SherComboBox
                    comboRef={schemeNameRef}
                    id="scheme-name"
                    value={extractSchemeName(formData.schemeName)}
                    onSelect={(opt) => {
                      if (!opt) {
                        handleClear();
                        return;
                      }
                      setFormData((prev) => ({
                        ...prev,
                        schemeName: opt.label || "",
                        schemeId: opt.value
                          ? opt.value.toString()
                          : prev.schemeId,
                      }));
                      if (opt.value) fetchSchemeById(opt.value);
                    }}
                    onClear={() => {
                      handleClear();
                    }}
                    onFreeInput={(txt) =>
                      setFormData((prev) => ({ ...prev, schemeName: txt }))
                    }
                    onKeyDown={(e) => handleKeyDown(e, "scheme-name")}
                    options={schemesList}
                    placeholder="Enter scheme name..."
                    showArrow={true}
                    clearable={true}
                    inputHeight={27}
                    inputWidth="100%"
                    inputStyle={{
                      borderRadius: 6,
                      fontFamily: "Inter, sans-serif",
                      fontSize: 12,
                      borderColor: "#cbd5e1",
                      width: "100%"
                    }}
                  />
                </div>
              </div>

              {/* From Date */}
              <div className="flex flex-col gap-1">
                <label
                  className="text-[11.5px] font-semibold text-slate-600 font-Inter"
                  htmlFor="scheme-from-date"
                >
                  {formData.validityType === "Tenure" ? "Launch Date" : "From Date"} <span className="text-red-500">*</span>
                </label>
                <input
                  id="scheme-from-date"
                  ref={schemeFromDateRef}
                  type="date"
                  className="w-full border border-slate-300 rounded-[6px] outline-none px-2.5 text-[12.5px] font-Inter box-border bg-white text-slate-800 transition-all duration-200 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12 read-only:bg-slate-50 read-only:text-slate-500 disabled:bg-slate-50 disabled:text-slate-500"
                  style={{ height: 27 }}
                  value={formData.fromDate}
                  onChange={(e) =>
                    handleInputChange("fromDate", e.target.value)
                  }
                  onKeyDown={(e) => handleKeyDown(e, "scheme-from-date")}
                  disabled={isLoading}
                />
              </div>

              {/* To Date */}
              <div className="flex flex-col gap-1">
                <label
                  className="text-[11.5px] font-semibold text-slate-600 font-Inter"
                  htmlFor="scheme-to-date"
                >
                  {formData.validityType === "Tenure" ? "Enrollment Cut-off (Optional)" : "To Date"} {formData.validityType === "Fixed" && <span className="text-red-500">*</span>}
                </label>
                <input
                  id="scheme-to-date"
                  ref={schemeToDateRef}
                  type="date"
                  className="w-full border border-slate-300 rounded-[6px] outline-none px-2.5 text-[12.5px] font-Inter box-border bg-white text-slate-800 transition-all duration-200 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12 read-only:bg-slate-50 read-only:text-slate-500 disabled:bg-slate-50 disabled:text-slate-500"
                  style={{ height: 27 }}
                  value={formData.toDate}
                  onChange={(e) => handleInputChange("toDate", e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, "scheme-to-date")}
                  disabled={isLoading}
                />
              </div>

              {/* Duration */}
              <div className="flex flex-col gap-1">
                <label
                  className="text-[11.5px] font-semibold text-slate-600 font-Inter"
                  htmlFor="scheme-duration"
                >
                  Duration ({formData.schemeType === "Daily" ? "Days" : "Months"}) <span className="text-red-500">*</span>
                  {formData.validityType === "Tenure" && (
                    <span className="text-[10px] text-[#1a3abf] font-normal ml-1">(Per Customer)</span>
                  )}
                </label>
                <input
                  id="scheme-duration"
                  ref={schemeDurationRef}
                  type="number"
                  className={`w-full border border-slate-300 rounded-[6px] outline-none px-2.5 text-[12.5px] font-Inter box-border ${
                    formData.validityType === "Tenure"
                      ? "bg-white text-slate-800 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12 font-bold"
                      : "bg-slate-50 text-slate-500"
                  }`}
                  style={{ height: 27 }}
                  placeholder="0"
                  value={formData.duration}
                  readOnly={formData.validityType === "Fixed"}
                  onChange={(e) => {
                    if (formData.validityType === "Tenure") {
                      const val = e.target.value.replace(/[^0-9]/g, "");
                      handleInputChange("duration", val);
                    }
                  }}
                  onKeyDown={(e) => handleKeyDown(e, "scheme-duration")}
                  disabled={isLoading}
                />
              </div>

              {/* Due Day (Only for Monthly Installments) */}
              {formData.schemeType === "Monthly" ? (
                <div className="flex flex-col gap-1">
                  <label
                    className="text-[11.5px] font-semibold text-slate-600 font-Inter"
                    htmlFor="scheme-due-date"
                  >
                    Due Day <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="scheme-due-date"
                    ref={schemeDueDateRef}
                    type="number"
                    className="w-full border border-slate-300 rounded-[6px] outline-none px-2.5 text-[12.5px] font-Inter box-border bg-white text-slate-800 transition-all duration-200 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12 read-only:bg-slate-50 read-only:text-slate-500 disabled:bg-slate-50 disabled:text-slate-500"
                    style={{ height: 27 }}
                    placeholder="Day (1-31)"
                    value={formData.dueDate}
                    onChange={(e) => handleDueDateChange(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, "scheme-due-date")}
                    min="1"
                    max="31"
                    disabled={isLoading}
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-1 opacity-50">
                  <label
                    className="text-[11.5px] font-semibold text-slate-400 font-Inter"
                  >
                    Due Day
                  </label>
                  <input
                    type="text"
                    className="w-full border border-slate-200 rounded-[6px] px-2.5 text-[12.5px] font-Inter bg-slate-50 text-slate-400"
                    style={{ height: 27 }}
                    placeholder="N/A (Daily Scheme)"
                    disabled
                  />
                </div>
              )}

              {/* Installment Amount */}
              <div className="flex flex-col gap-1">
                <label
                  className="text-[11.5px] font-semibold text-slate-600 font-Inter"
                  htmlFor="installment-amount"
                >
                  Installment Amount <span className="text-red-500">*</span>
                </label>
                <input
                  id="installment-amount"
                  ref={installmentAmountRef}
                  type="number"
                  className="w-full border border-slate-300 rounded-[6px] outline-none px-2.5 text-[12.5px] font-Inter box-border bg-white text-slate-800 transition-all duration-200 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12"
                  style={{ height: 27 }}
                  placeholder="0.00"
                  value={formData.installmentAmount}
                  onChange={(e) => handleInputChange("installmentAmount", e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, "installment-amount")}
                  disabled={isLoading}
                />
              </div>

              {/* Maturity Value */}
              <div className="flex flex-col gap-1">
                <label
                  className="text-[11.5px] font-semibold text-slate-600 font-Inter"
                  htmlFor="maturity-amount"
                >
                  Maturity Value
                </label>
                <input
                  id="maturity-amount"
                  ref={maturityAmountRef}
                  type="number"
                  className="w-full border border-slate-300 rounded-[6px] outline-none px-2.5 text-[12.5px] font-Inter box-border bg-white text-slate-800 transition-all duration-200 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12"
                  style={{ height: 27 }}
                  placeholder="0.00"
                  value={formData.maturityAmount}
                  onChange={(e) => handleInputChange("maturityAmount", e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, "maturity-amount")}
                  disabled={isLoading}
                />
              </div>

                            {/* Narration */}
              <div className="col-span-2 flex flex-col gap-1">
                <label
                  className="text-[11.5px] font-semibold text-slate-600 font-Inter"
                  htmlFor="scheme-narration"
                >
                  Narration
                </label>
                <textarea
                  id="scheme-narration"
                  ref={schemeNarrationRef}
                  rows={1}
                  className="w-full border border-slate-300 rounded-[6px] outline-none px-2.5 py-1 text-[12.5px] font-Inter box-border bg-white text-slate-800 transition-all duration-200 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12 read-only:bg-slate-50 read-only:text-slate-500 disabled:bg-slate-50 disabled:text-slate-500"
                  style={{
                    height: 28,
                    resize: "none",
                  }}
                  value={formData.narration}
                  onChange={(e) => handleInputChange("narration", e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, "scheme-narration")}
                  disabled={isLoading}
                  placeholder="Scheme narration..."
                />
              </div>

              </div>

                {/* Required fields */}
                <div className="text-[10px] text-gray-400 font-Inter mt-auto pt-2">
                  * Required fields
                </div>
              </>
            )}

            {/* TAB 2: ACCOUNT SERIES CONFIGURATION */}
            {activeTab === "series" && (
              <div className="flex flex-col flex-1 gap-2.5 py-1 h-full">
                {/* Information Header Card */}
                <div className="bg-blue-50/70 border border-blue-200/80 rounded-lg p-2.5 flex items-start gap-2.5">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5 text-[#1a3abf]">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[11.5px] font-bold text-blue-900 font-Inter">
                      Scheme Account Series Configuration
                    </span>
                    <span className="text-[10.5px] text-blue-700 font-Inter leading-snug">
                      Set a prefix, starting number, and suffix for customers registering under this scheme.
                    </span>
                  </div>
                </div>

                {/* Input Fields Card */}
                <div className="p-3 bg-slate-50/70 rounded-lg border border-slate-200 flex flex-col gap-2">
                  <div className="text-[11px] font-bold text-slate-700 font-Inter">
                    Series Parameters
                  </div>
                  <div className="grid grid-cols-3 gap-2.5">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10.5px] font-semibold text-slate-600 font-Inter" htmlFor="account-prefix">
                        Account Prefix
                      </label>
                      <input
                        id="account-prefix"
                        ref={accountPrefixRef}
                        type="text"
                        className="w-full border border-slate-300 rounded-[6px] outline-none px-2.5 text-[12px] font-mono font-bold uppercase bg-white text-slate-800 transition-all duration-200 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12"
                        style={{ height: 28 }}
                        placeholder="e.g. SW/"
                        value={formData.accountPrefix || ""}
                        onChange={(e) => handleInputChange("accountPrefix", e.target.value.toUpperCase())}
                        onKeyDown={(e) => handleKeyDown(e, "account-prefix")}
                      />
                      <span className="text-[9px] text-slate-400 font-Inter">e.g. SW/, GL-, SCH/</span>
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-[10.5px] font-semibold text-slate-600 font-Inter" htmlFor="starting-account-no">
                        Starting Account No.
                      </label>
                      <input
                        id="starting-account-no"
                        ref={startingAccountNoRef}
                        type="number"
                        min="0"
                        className="w-full border border-slate-300 rounded-[6px] outline-none px-2.5 text-[12px] font-mono font-bold bg-white text-slate-800 transition-all duration-200 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12"
                        style={{ height: 28 }}
                        placeholder="0"
                        value={formData.startingAccountNo ?? 0}
                        onChange={(e) => handleInputChange("startingAccountNo", e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, "starting-account-no")}
                      />
                      <span className="text-[9px] text-slate-400 font-Inter">Starts at 1 if 0</span>
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-[10.5px] font-semibold text-slate-600 font-Inter" htmlFor="account-suffix">
                        Account Suffix
                      </label>
                      <input
                        id="account-suffix"
                        ref={accountSuffixRef}
                        type="text"
                        className="w-full border border-slate-300 rounded-[6px] outline-none px-2.5 text-[12px] font-mono font-bold uppercase bg-white text-slate-800 transition-all duration-200 focus:border-[#1a3abf] focus:ring-1 focus:ring-[#1a3abf]/12"
                        style={{ height: 28 }}
                        placeholder="e.g. /24"
                        value={formData.accountSuffix || ""}
                        onChange={(e) => handleInputChange("accountSuffix", e.target.value.toUpperCase())}
                        onKeyDown={(e) => handleKeyDown(e, "account-suffix")}
                      />
                      <span className="text-[9px] text-slate-400 font-Inter">e.g. /24, /2024, -A</span>
                    </div>
                  </div>
                </div>

                {/* Sequence Preview Box */}
                <div className="p-3 bg-gradient-to-r from-blue-50/60 to-indigo-50/60 rounded-lg border border-blue-200/80 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10.5px] font-bold text-slate-700 uppercase tracking-wider font-Inter">
                      Generated Account Sequence Preview
                    </span>
                    <span className="text-[10px] font-semibold text-blue-700 font-Inter">
                      Live Output
                    </span>
                  </div>

                  <div className="flex items-center gap-2 pt-0.5">
                    <div className="flex-1 bg-white p-2 rounded-md border border-blue-200/80 flex flex-col items-center shadow-3xs">
                      <span className="text-[9px] text-slate-400 font-medium font-Inter">1st Customer</span>
                      <span className="font-mono text-[12px] font-bold text-blue-900 mt-0.5">
                        {formData.accountPrefix || ""}{String(formData.startingAccountNo > 0 ? formData.startingAccountNo : 1).padStart(3, "0")}{formData.accountSuffix || ""}
                      </span>
                    </div>
                    <span className="text-slate-400 font-bold text-xs">→</span>
                    <div className="flex-1 bg-white p-2 rounded-md border border-blue-200/80 flex flex-col items-center shadow-3xs">
                      <span className="text-[9px] text-slate-400 font-medium font-Inter">2nd Customer</span>
                      <span className="font-mono text-[12px] font-bold text-blue-900 mt-0.5">
                        {formData.accountPrefix || ""}{String((formData.startingAccountNo > 0 ? Number(formData.startingAccountNo) : 1) + 1).padStart(3, "0")}{formData.accountSuffix || ""}
                      </span>
                    </div>
                    <span className="text-slate-400 font-bold text-xs">→</span>
                    <div className="flex-1 bg-white p-2 rounded-md border border-blue-200/80 flex flex-col items-center shadow-3xs">
                      <span className="text-[9px] text-slate-400 font-medium font-Inter">3rd Customer</span>
                      <span className="font-mono text-[12px] font-bold text-blue-900 mt-0.5">
                        {formData.accountPrefix || ""}{String((formData.startingAccountNo > 0 ? Number(formData.startingAccountNo) : 1) + 2).padStart(3, "0")}{formData.accountSuffix || ""}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="text-[10px] text-slate-400 font-Inter mt-auto pt-2 flex items-center gap-1.5 border-t border-slate-100">
                  <span className="font-bold text-blue-600">Tip:</span> If left empty, account numbers will follow standard sequence (001, 002, 003...).
                </div>
              </div>
            )}
          </div>

          {/* ── RIGHT CARD: SCHEME SUMMARY ─────────────────────────────────── */}
          <div
            className="rounded-lg p-2.5 flex flex-col gap-2 box-border border h-full"
            style={{
              width: 250,
              minWidth: 250,
              background: "#f0f5ff",
              borderColor: "#dbeafe",
            }}
          >
            {/* Section heading */}
            <div
              className="flex items-center gap-2 pb-1.5 border-b border-dashed border-blue-200 mb-1"
              style={{ borderBottomColor: "#bfdbfe" }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1a3abf"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span className="text-[10.5px] font-bold text-[#1a3abf] uppercase tracking-widest font-Inter">
                Scheme Summary
              </span>
            </div>

            {/* Duration Summary */}
            <div className="bg-white rounded-[6px] border border-gray-100 p-2 flex items-center gap-2.5 shadow-sm">
              <div className="w-7 h-7 rounded-[5px] bg-blue-50 flex items-center justify-center flex-shrink-0">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1a3abf"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-[9.5px] text-gray-500 font-medium font-Inter">
                  Duration
                </span>
                <div className="flex items-baseline gap-1">
                  <span className="text-base font-bold text-blue-900 font-Inter leading-none">
                    {formData.duration || "0"}
                  </span>
                  <span className="text-[9.5px] text-gray-500 font-Inter">
                    {formData.schemeType === "Daily" ? "Days" : "Months"}
                  </span>
                </div>
              </div>
            </div>

            {/* Period Summary */}
            <div className="bg-white rounded-[6px] border border-gray-100 p-2 flex items-center gap-2.5 shadow-sm">
              <div className="w-7 h-7 rounded-[5px] bg-blue-50 flex items-center justify-center flex-shrink-0">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1a3abf"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-[9.5px] text-gray-500 font-medium font-Inter">
                  Period
                </span>
                <span className="text-[10.5px] font-semibold text-gray-700 font-Inter leading-snug">
                  {formData.fromDate && formData.toDate
                    ? `${formatDisplayDate(formData.fromDate)} to ${formatDisplayDate(formData.toDate)}`
                    : "Not Set"}
                </span>
              </div>
            </div>

            {/* Due Day Summary (Only for Monthly) */}
            {formData.schemeType === "Monthly" && (
              <div className="bg-white rounded-[6px] border border-gray-100 p-2 flex items-center gap-2.5 shadow-sm">
                <div className="w-7 h-7 rounded-[5px] bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#1a3abf"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-[9.5px] text-gray-500 font-medium font-Inter">
                    Due Day
                  </span>
                  <span className="text-[10.5px] font-bold text-gray-700 font-Inter">
                    {formData.dueDate ? `Day ${formData.dueDate}` : "Not Set"}
                  </span>
                </div>
              </div>
            )}

            {/* Maturity Amount Summary */}
            <div className="bg-white rounded-[6px] border border-gray-100 p-2 flex items-center gap-2.5 shadow-sm">
              <div className="w-7 h-7 rounded-[5px] bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <span className="text-emerald-600 font-bold text-xs">₹</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[9.5px] text-gray-500 font-medium font-Inter">
                  Maturity Value
                </span>
                <span className="text-[11.5px] font-bold text-emerald-700 font-Inter">
                  ₹{parseFloat(formData.maturityAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {/* Account Series Summary */}
            <div className="bg-white rounded-[6px] border border-gray-100 p-2 flex items-center gap-2.5 shadow-sm">
              <div className="w-7 h-7 rounded-[5px] bg-purple-50 flex items-center justify-center flex-shrink-0">
                <span className="text-purple-600 font-bold text-xs font-mono">#</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[9.5px] text-gray-500 font-medium font-Inter">
                  Account Series
                </span>
                <span className="text-[10px] font-bold text-purple-800 font-mono">
                  {formData.accountPrefix || formData.accountSuffix || formData.startingAccountNo > 0
                    ? `${formData.accountPrefix || ""}${String(formData.startingAccountNo > 0 ? formData.startingAccountNo : 1).padStart(3, "0")}${formData.accountSuffix || ""}`
                    : "Standard (001...)"}
                </span>
              </div>
            </div>

            {/* Info Message Box */}
            <div className="bg-blue-100/50 border border-blue-200/60 rounded-[6px] p-1.5 flex items-start gap-1.5 mt-auto">
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#2563eb"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mt-0.5 flex-shrink-0"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <span className="text-[8.5px] text-blue-700 font-Inter leading-normal">
                Scheme will be active for the selected duration.
              </span>
            </div>
          </div>
        </div>
        {/* end card containers body */}
      </div>
    </>
  );
}

export default SchemeManagement;
